import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi, predictionManagerAxiosApi } from 'src/Config/axiosApi';
import { PositionsGroupedByPair, PerformanceData, PredictionCashflow } from '../types';

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

interface PositionsFilters {
  status?: string;
  platform?: string;
}

export function usePositionsGrouped(filters?: PositionsFilters) {
  return useQuery(
    ['positions-grouped', filters],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: filters });
      const groups = data.filter(g => g._id != null);
      const unmatched = data.filter(g => g._id == null);
      return { groups, unmatched };
    },
    {
      keepPreviousData: true,
      // Open positions can close outside this view; poll so stale rows disappear automatically.
      refetchInterval: filters?.status === 'open' ? 10000 : false,
      refetchIntervalInBackground: filters?.status === 'open',
    }
  );
}

export function usePositionsEnded() {
  return useQuery(
    ['positions-ended'],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: { status: 'ended' } });
      return data;
    },
    { keepPreviousData: true }
  );
}

export function usePositionStats() {
  return useQuery(['position-stats'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PositionStats>(
        '/positions/stats'
      );
    return data;
  });
}

export function usePerformance() {
  return useQuery(['position-performance'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PerformanceData>(
        '/positions/performance'
      );
    return data;
  }, { refetchInterval: 30000 });
}

export function useCashflows() {
  return useQuery(['position-cashflows'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCashflow[]>(
        '/positions/cashflows'
      );
    return data;
  });
}

export function useCreateCashflow() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { platform: string; type: string; amountUsd: number; note?: string; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post('/positions/cashflows', body);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('position-cashflows');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}

type GroupedPositionsCache = {
  groups: PositionsGroupedByPair[];
  unmatched: PositionsGroupedByPair[];
};

/**
 * Per-position opt-in for TNA (time-to-no-arb) alerts. Default OFF; when OFF
 * the MTNA/TTNA cells render `-`. PATCHes the prediction-arbitrage-manager
 * endpoint and optimistically flips the flag on the matching grouped row so
 * the switch reflects the change immediately, rolling back on error.
 */
export function useToggleTnaAlerts() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, enabled }: { pairId: string; enabled: boolean }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/${pairId}/tna-alerts`,
        { enabled }
      );
      return data;
    },
    {
      mutationKey: 'positions:toggle-tna-alerts',
      onMutate: async ({ pairId, enabled }) => {
        await qc.cancelQueries('positions-grouped');
        const snapshot = qc.getQueriesData<GroupedPositionsCache>(
          'positions-grouped'
        );
        const flip = (g: PositionsGroupedByPair) =>
          g._id === pairId ? { ...g, tnaAlertsEnabled: enabled } : g;
        qc.setQueriesData<GroupedPositionsCache | undefined>(
          'positions-grouped',
          (prev) =>
            prev
              ? {
                  groups: prev.groups.map(flip),
                  unmatched: prev.unmatched.map(flip),
                }
              : prev
        );
        return { snapshot };
      },
      onError: (_err, _vars, context) => {
        const ctx = context as
          | { snapshot: [unknown, GroupedPositionsCache | undefined][] }
          | undefined;
        ctx?.snapshot.forEach(([key, data]) => qc.setQueryData(key as never, data));
      },
      onSettled: () => {
        qc.invalidateQueries('positions-grouped');
      },
    }
  );
}

export function useTogglePairArchive() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, archived }: { pairId: string; archived: boolean }) => {
      const { data } = await predictionManagerAxiosApi.patch(
        `/pairs/${pairId}`,
        { archived }
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

export function useUpdatePairExpiry() {
  const qc = useQueryClient();
  return useMutation(
    async ({ pairId, expiryDate }: { pairId: string; expiryDate: string }) => {
      const { data } = await predictionManagerAxiosApi.patch(`/pairs/${pairId}`, {
        expiryDate,
      });
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
      }
    }
  );
}

/**
 * Manually close an open position. The exchange API may still report it
 * as open (e.g. you exited via the exchange UI but the API hasn't dropped
 * the row yet, or you want to hide a stuck row before the absence-counter
 * / endDate fallback fires). Hits POST /positions/:id/close which sets
 * `closedManually: true` so subsequent sync cycles skip the position and
 * cannot revert status back to 'open'.
 *
 * Does NOT trade. The user has already exited (or will exit) on the
 * exchange.
 */
export function useClosePositionManually() {
  const qc = useQueryClient();
  return useMutation(
    async (positionId: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        `/positions/${positionId}/close`
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
      },
    }
  );
}

export function useSyncPositions() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.post('/positions/sync');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
        qc.invalidateQueries('position-performance');
      }
    }
  );
}
